import React, { useState } from 'react';
import { FiX, FiMapPin, FiPhone, FiExternalLink, FiSearch } from 'react-icons/fi';

const CompanySelectModal = ({ companies, isOpen, onClose, onSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');

  if (!isOpen) return null;

  // Get unique locations
  const locations = [...new Set(companies.map(c => c.location).filter(Boolean))];

  // Filter companies
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.location?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesLocation = selectedLocation === 'all' || company.location === selectedLocation;
    
    return matchesSearch && matchesLocation;
  });

  const handleSelect = (company) => {
    if (onSelect) {
      // If onSelect callback is provided, use it (no redirect)
      onSelect(company);
      return;
    }
    
    // Otherwise, navigate to company's subdomain
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
      // Local development
      window.location.href = `http://${company.slug}.localhost:${port || '3000'}`;
    } else {
      // Production - detect base domain
      const baseDomain = hostname.includes('satyasreeman.com') 
        ? 'satyasreeman.com' 
        : hostname.includes('rentsacar.com')
        ? 'rentsacar.com'
        : 'carbooking.com';
      window.location.href = `https://${company.slug}.${baseDomain}`;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Select a Rental Company</h2>
              <p className="text-gray-600 mt-1">Choose from our trusted partners in your area</p>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <FiX size={24} />
              </button>
            )}
          </div>
          
          {/* Filters */}
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search by name or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              {locations.length > 0 && (
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Locations</option>
                  {locations.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
          
          {/* Companies Grid */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {filteredCompanies.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredCompanies.map((company) => (
                  <button
                    key={company.id}
                    onClick={() => handleSelect(company)}
                    className="flex items-start gap-4 p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-all text-left group"
                  >
                    {/* Logo */}
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                      {company.logo ? (
                        <img src={company.logo} alt={company.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-2xl font-bold text-gray-400">
                          {company.name[0]}
                        </span>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <h3 className="font-semibold text-gray-900 group-hover:text-primary-700">
                          {company.name}
                        </h3>
                        <FiExternalLink className="text-gray-400 group-hover:text-primary-600 flex-shrink-0" size={16} />
                      </div>
                      
                      {company.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2 whitespace-pre-wrap">
                          {company.description}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                        {company.location && (
                          <span className="flex items-center gap-1">
                            <FiMapPin size={14} />
                            {company.location}
                          </span>
                        )}
                        {company.phone && (
                          <span className="flex items-center gap-1">
                            <FiPhone size={14} />
                            {company.phone}
                          </span>
                        )}
                      </div>
                      
                      {/* Service Areas Tags */}
                      {company.serviceAreas?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {company.serviceAreas.slice(0, 3).map((area, index) => (
                            <span 
                              key={index}
                              className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600"
                            >
                              {area}
                            </span>
                          ))}
                          {company.serviceAreas.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                              +{company.serviceAreas.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FiSearch size={48} className="mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No companies found</h3>
                <p>Try adjusting your search or location filter</p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl">
            <p className="text-center text-sm text-gray-500">
              {filteredCompanies.length} rental {filteredCompanies.length === 1 ? 'company' : 'companies'} available
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySelectModal;
